<script setup>
import ComboboxField from '@js/components/fields/ComboboxField.vue';
import ListboxField from '@js/components/fields/ListboxField.vue';
import MonthField from '@js/components/fields/MonthField.vue';
import { computed, ref, watch } from 'vue';

const { filterDefinitions, filters, setFilters } = defineProps({
    filterDefinitions: {
        type: Array,
        required: true,
    },
    filters: {
        type: Array,
        required: true,
    },
    setFilters: {
        type: Function,
        required: true,
    },
});

const currentFilters = ref(initCurrentFilters());

const quickFilterDefinitions = computed(() => {
    return filterDefinitions.filter(
        (filter) => filter.quick && filter.quick === true,
    );
});

function initCurrentFilters() {
    let tmpCurrentFilters = {};

    for (const filterDefinition of Object.values(filterDefinitions)) {
        let filterIndex = filters.findIndex(
            (filter) => filter.id === filterDefinition.id,
        );

        if (filterIndex === -1) {
            if (filterDefinition['type'] === 'range') {
                tmpCurrentFilters[filterDefinition.id] = [null, null];
            } else {
                tmpCurrentFilters[filterDefinition.id] = null;
            }
        } else {
            if (filterDefinition['type'] === 'range') {
                tmpCurrentFilters[filterDefinition.id] = [
                    filters[filterIndex].value[0],
                    filters[filterIndex].value[1],
                ];
            } else if (filterDefinition['type'] === 'month') {
                tmpCurrentFilters[filterDefinition.id] = {
                    month: filters[filterIndex].value.month,
                    year: filters[filterIndex].value.year,
                };
            } else if (
                filterDefinition['type'] !== 'listbox' &&
                filterDefinition['type'] !== 'combobox'
            ) {
                tmpCurrentFilters[filterDefinition.id] =
                    filters[filterIndex].value;
            } else {
                let label;

                if (filterDefinition.options.length > 0) {
                    let search = filterDefinition.options.find(
                        (option) => option.value === filters[filterIndex].value,
                    );

                    label = search ? search.label : filters[filterIndex].value;
                } else {
                    label = filters[filterIndex].value;
                }

                tmpCurrentFilters[filterDefinition.id] = {
                    value: filters[filterIndex].value,
                    label: label,
                };
            }
        }
    }

    return tmpCurrentFilters;
}

watch(currentFilters.value, () => {
    let tmpFilters = filters;

    for (let filterDefinition of Object.values(filterDefinitions)) {
        let currentFilterOption = currentFilters.value[filterDefinition.id];
        let hasFilter =
            filterDefinition.type === 'range'
                ? currentFilterOption[0] !== null ||
                  currentFilterOption[1] !== null
                : currentFilterOption !== null &&
                  currentFilterOption !== undefined;
        let filterIndex = tmpFilters.findIndex(
            (filter) => filter.id === filterDefinition.id,
        );

        if (hasFilter) {
            if (filterIndex === -1) {
                let value;

                if (Array.isArray(currentFilterOption)) {
                    value = currentFilterOption;
                } else if (typeof currentFilterOption === 'object') {
                    if (filterDefinition.formattedValue) {
                        value = filterDefinition.formattedValue(
                            currentFilterOption.value,
                        );
                    } else if (
                        typeof currentFilterOption.value === 'undefined'
                    ) {
                        value = currentFilterOption;
                    } else {
                        value = currentFilterOption.value;
                    }
                } else {
                    if (filterDefinition.formattedValue) {
                        value =
                            filterDefinition.formattedValue(
                                currentFilterOption,
                            );
                    } else {
                        value = currentFilterOption;
                    }
                }

                tmpFilters.push({
                    id: filterDefinition.id,
                    column: filterDefinition.column,
                    label: filterDefinition.label,
                    operator: filterDefinition.operator,
                    value: value,
                    valueLabel:
                        typeof currentFilterOption === 'object'
                            ? currentFilterOption.label
                            : { ...currentFilterOption },
                });
            } else {
                if (Array.isArray(currentFilterOption)) {
                    tmpFilters[filterIndex].value = currentFilterOption;
                } else {
                    tmpFilters[filterIndex].value =
                        typeof currentFilterOption === 'object' &&
                        typeof currentFilterOption.value !== 'undefined'
                            ? currentFilterOption.value
                            : currentFilterOption;
                    tmpFilters[filterIndex].valueLabel =
                        typeof currentFilterOption === 'object' &&
                        typeof currentFilterOption.label !== 'undefined'
                            ? currentFilterOption.label
                            : currentFilterOption;
                }
            }
        } else {
            if (filterIndex !== -1) {
                tmpFilters.splice(filterIndex, 1);
            }
        }
    }

    setFilters(tmpFilters);
});
</script>

<template>
    <div
        v-if="quickFilterDefinitions && quickFilterDefinitions.length > 0"
        class="flex flex-wrap gap-2"
    >
        <template
            v-for="(quickFilterDefinition, index) in quickFilterDefinitions"
            :key="index"
        >
            <ListboxField
                v-if="quickFilterDefinition.type === 'listbox'"
                v-model="currentFilters[quickFilterDefinition.id]"
                :options="quickFilterDefinition.options"
                :label="quickFilterDefinition.label"
                :field-name="quickFilterDefinition.id"
                :field-id="quickFilterDefinition.id"
                by="value"
                has-empty-option
                size="small"
                inline-label
                class="!m-0"
            >
                <template #buttonContent>
                    {{
                        currentFilters[quickFilterDefinition.id]
                            ? currentFilters[quickFilterDefinition.id].label
                            : '&nbsp;'
                    }}
                </template>

                <template #optionContent="{ option }">
                    {{ option.label }}
                </template>
            </ListboxField>

            <ComboboxField
                v-if="quickFilterDefinition.type === 'combobox'"
                v-model="currentFilters[quickFilterDefinition.id]"
                :options="quickFilterDefinition.options"
                :label="quickFilterDefinition.label"
                :field-name="quickFilterDefinition.id"
                :field-id="quickFilterDefinition.id"
                by="value"
                filtered-option-key="label"
                size="small"
                has-empty-option
                inline-label
                class="!m-0"
            >
                <template #optionContent="{ option }">
                    <span :title="option.label">{{ option.label }}</span>
                </template>
            </ComboboxField>

            <MonthField
                v-if="quickFilterDefinition.type === 'month'"
                v-model="currentFilters[quickFilterDefinition.id]"
                :label="quickFilterDefinition.label"
                :field-name="quickFilterDefinition.id"
                :field-id="quickFilterDefinition.id"
                size="small"
                :has-empty-option="
                    quickFilterDefinition.hasEmptyOption === undefined ||
                    quickFilterDefinition.hasEmptyOption === true
                "
                inline-label
                class="!m-0"
                :max-date="
                    'maxDate' in quickFilterDefinition
                        ? quickFilterDefinition.maxDate
                        : null
                "
            />
        </template>
    </div>
</template>
